import { t as uiText } from '../../i18n';
import React, { useState } from 'react';

type ErrorCode =
  | 'ORIGIN_NOT_ALLOWED'
  | 'TOKEN_INVALID'
  | 'TOKEN_DISABLED'
  | 'PUBLISH_ID_NOT_FOUND'
  | 'WORKSPACE_MISMATCH'
  | 'MISSING_PARAMS'
  | 'RATE_LIMITED'
  | 'VALIDATE_INTERNAL_ERROR'
  | 'UNKNOWN';

interface BilingualText {
  ja: string;
  en: string;
}

interface ErrorMessage {
  title: BilingualText;
  body: BilingualText;
  hint: BilingualText;
}

const ERROR_MESSAGES: Record<ErrorCode, ErrorMessage> = {
  ORIGIN_NOT_ALLOWED: {
    title: {
      get ja() { return uiText("Unavailable"); },
      en: 'Service not available',
    },
    body: {
      get ja() { return uiText("The AI assistant is not available on this site."); },
      en: 'The AI assistant is not available on this site.',
    },
    hint: {
      get ja() { return uiText("Please contact the site administrator."); },
      en: 'Please contact the site administrator.',
    },
  },
  TOKEN_INVALID: {
    title: { get ja() { return uiText("Unavailable"); }, en: 'Service not available' },
    body: {
      get ja() { return uiText("The AI assistant settings are invalid."); },
      en: 'The AI assistant is not configured correctly.',
    },
    hint: {
      get ja() { return uiText("Please contact the site administrator."); },
      en: 'Please contact the site administrator.',
    },
  },
  TOKEN_DISABLED: {
    title: { get ja() { return uiText("Unavailable"); }, en: 'Service not available' },
    body: {
      get ja() { return uiText("The AI assistant has been suspended."); },
      en: 'The AI assistant has been disabled.',
    },
    hint: {
      get ja() { return uiText("Please contact the site administrator."); },
      en: 'Please contact the site administrator.',
    },
  },
  PUBLISH_ID_NOT_FOUND: {
    title: { get ja() { return uiText("Unavailable"); }, en: 'Service not available' },
    body: {
      get ja() { return uiText("AI assistant public settings were not found."); },
      en: 'The AI assistant configuration was not found.',
    },
    hint: {
      get ja() { return uiText("Please contact the site administrator."); },
      en: 'Please contact the site administrator.',
    },
  },
  WORKSPACE_MISMATCH: {
    title: { get ja() { return uiText("Unavailable"); }, en: 'Service not available' },
    body: {
      get ja() { return uiText("The AI assistant settings are inconsistent."); },
      en: 'There is a configuration mismatch.',
    },
    hint: {
      get ja() { return uiText("Please contact the site administrator."); },
      en: 'Please contact the site administrator.',
    },
  },
  MISSING_PARAMS: {
    title: { get ja() { return uiText("Unavailable"); }, en: 'Service not available' },
    body: {
      get ja() { return uiText("The AI assistant settings are incomplete."); },
      en: 'The AI assistant configuration is incomplete.',
    },
    hint: {
      get ja() { return uiText("Please contact the site administrator."); },
      en: 'Please contact the site administrator.',
    },
  },
  RATE_LIMITED: {
    title: { get ja() { return uiText("Temporarily unavailable"); }, en: 'Temporarily unavailable' },
    body: {
      get ja() { return uiText("Traffic is high. Please try again later."); },
      en: 'Too many requests. Please try again later.',
    },
    hint: {
      get ja() { return uiText("Wait a moment and try again."); },
      en: 'Please try again in a few moments.',
    },
  },
  VALIDATE_INTERNAL_ERROR: {
    title: { get ja() { return uiText("Temporarily unavailable"); }, en: 'Temporarily unavailable' },
    body: {
      get ja() { return uiText("A network error occurred."); },
      en: 'A communication error occurred.',
    },
    hint: {
      get ja() { return uiText("Please try again later."); },
      en: 'Please try again later.',
    },
  },
  UNKNOWN: {
    title: { get ja() { return uiText("Unavailable"); }, en: 'Service not available' },
    body: {
      get ja() { return uiText("An unexpected error occurred."); },
      en: 'An unexpected error occurred.',
    },
    hint: {
      get ja() { return uiText("Please contact the site administrator."); },
      en: 'Please contact the site administrator.',
    },
  },
};

function getMessage(code: string): ErrorMessage {
  if (code in ERROR_MESSAGES) return ERROR_MESSAGES[code as ErrorCode];
  return ERROR_MESSAGES.UNKNOWN;
}

interface WidgetErrorViewProps {
  errorMode: string;
  errorId?: string | null;
}

const REPORT_ENDPOINT = '/report-error';

const WidgetErrorView: React.FC<WidgetErrorViewProps> = ({ errorMode, errorId }) => {
  const [reportState, setReportState] = useState<'idle' | 'sending' | 'done' | 'failed'>('idle');
  const [hovered, setHovered] = useState(false);
  const msg = getMessage(errorMode);

  const canReport = !!errorId && reportState === 'idle';

  const handleReport = async () => {
    if (!errorId) return;
    setReportState('sending');
    try {
      const res = await fetch(REPORT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        body: JSON.stringify({ errorId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data && data.ok) {
        setReportState('done');
      } else {
        setReportState('failed');
      }
    } catch {
      setReportState('failed');
    }
  };

  if (reportState === 'done') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={iconWrapStyle('success')} aria-hidden>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={titleStyle}>{uiText("Thank you for the report")}</h2>
          <p style={subtitleStyle}>Thank you for your report</p>
          <p style={bodyStyle}>
            {uiText("We'll review this and follow up.")}
            <br />
            <span style={bodyEnStyle}>We will review and address this issue.</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={iconWrapStyle('warn')} aria-hidden>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        <h2 style={titleStyle}>{msg.title.ja}</h2>
        <p style={subtitleStyle}>{msg.title.en}</p>

        <div style={dividerStyle} />

        <p style={bodyStyle}>
          {msg.body.ja}
          <br />
          <span style={bodyEnStyle}>{msg.body.en}</span>
        </p>
        <p style={hintStyle}>
          {msg.hint.ja}
          <br />
          <span style={bodyEnStyle}>{msg.hint.en}</span>
        </p>

        {canReport && (
          <button
            type="button"
            onClick={handleReport}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
              ...buttonStyle,
              background: hovered ? 'var(--workspace-primary-dark, #b89a7a)' : 'var(--workspace-primary, #cfae91)',
            }}
            aria-label="Report this error"
          >
            {uiText("Report this error")}
            <span style={buttonSubLabelStyle}>Report this error</span>
          </button>
        )}
        {reportState === 'sending' && (
          <p style={statusTextStyle}>{uiText("Sending... / Sending...")}</p>
        )}
        {reportState === 'failed' && (
          <>
            <button
              type="button"
              onClick={handleReport}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              style={{
                ...buttonStyle,
                background: hovered ? 'var(--workspace-primary-dark, #b89a7a)' : 'var(--workspace-primary, #cfae91)',
              }}
            >
              {uiText("Resend")}
              <span style={buttonSubLabelStyle}>Try again</span>
            </button>
            <p style={{ ...statusTextStyle, color: '#c0392b' }}>
              {uiText("Failed to send. / Failed to send.")}
            </p>
          </>
        )}

        {errorId && (
          <p style={errorIdStyle}>
            Error ID: <code style={errorIdCodeStyle}>{errorId}</code>
          </p>
        )}
      </div>
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100%',
  width: '100%',
  padding: '24px',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-sans)',
  color: '#334155',
  background: 'transparent',
};

const cardStyle: React.CSSProperties = {
  maxWidth: 360,
  width: '100%',
  textAlign: 'center',
  padding: '8px 4px',
};

const iconWrapStyle = (variant: 'warn' | 'success'): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: '50%',
  background:
    variant === 'success'
      ? 'rgba(207, 174, 145, 0.18)'
      : 'var(--workspace-bg-soft, #E9D8C9)',
  color: 'var(--workspace-primary-dark, #b89a7a)',
  margin: '0 auto 16px',
});

const titleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-heading, var(--font-sans))',
  fontSize: 17,
  fontWeight: 600,
  margin: '0 0 4px 0',
  lineHeight: 1.45,
  color: '#334155',
  letterSpacing: '0.01em',
};

const subtitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-en, var(--font-sans))',
  fontSize: 12,
  fontWeight: 500,
  margin: '0 0 16px 0',
  color: '#94a3b8',
  letterSpacing: '0.04em',
};

const dividerStyle: React.CSSProperties = {
  width: 32,
  height: 1,
  background: 'var(--workspace-border-soft, #f5f1ed)',
  margin: '4px auto 16px',
  border: 'none',
};

const bodyStyle: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.7,
  margin: '0 0 10px 0',
  color: '#475569',
};

const bodyEnStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: '#94a3b8',
  fontFamily: 'var(--font-en, var(--font-sans))',
};

const hintStyle: React.CSSProperties = {
  ...bodyStyle,
  color: '#64748b',
  margin: '0 0 20px 0',
};

const buttonStyle: React.CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  marginTop: 4,
  marginBottom: 8,
  padding: '11px 22px',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'var(--font-heading, var(--font-sans))',
  color: 'var(--workspace-text-on-primary, #ffffff)',
  background: 'var(--workspace-primary, #cfae91)',
  border: 'none',
  borderRadius: 999,
  cursor: 'pointer',
  letterSpacing: '0.02em',
  boxShadow: '0 2px 8px rgba(207, 174, 145, 0.35)',
  transition: 'background 0.15s ease, transform 0.05s ease, box-shadow 0.15s ease',
  lineHeight: 1.2,
};

const buttonSubLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  opacity: 0.85,
  letterSpacing: '0.05em',
};

const statusTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#94a3b8',
  margin: '8px 0 0',
};

const errorIdStyle: React.CSSProperties = {
  marginTop: 20,
  fontSize: 10.5,
  color: '#cbd5e1',
  wordBreak: 'break-all',
  letterSpacing: '0.02em',
};

const errorIdCodeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10.5,
  color: '#94a3b8',
  background: 'transparent',
  padding: 0,
};

export default WidgetErrorView;
